import orderBy from 'lodash/orderBy';
import filter from 'lodash/filter';
import toLower from 'lodash/toLower';
import includes from 'lodash/includes';

export const sortUsers = (users, sortBy, sortOrder, accountMap = {}) => {
  if (!users || users.length === 0) {
    return [];
  }

  // Custom sorting for specific fields
  if (sortBy === 'accounts') {
    // Sort by first account name (case-insensitive), fall back to ID
    const sorted = orderBy(users, [user => {
      const firstAccount = user.account;
      if (!firstAccount) {return '';}

      // Get the account ID
      const accountId = typeof firstAccount === 'string' ? firstAccount : firstAccount.id;

      // Try to get the account name from accountMap
      if (accountId && accountMap && accountMap[accountId] && accountMap[accountId].name) {
        return toLower(accountMap[accountId].name);
      }
      // Fall back to name property if it exists in the account object
      if (typeof firstAccount === 'object' && firstAccount.name) {
        return toLower(firstAccount.name);
      }
      // Fall back to ID
      return toLower(accountId || '');
    }], [sortOrder]);
    return sorted;
  }

  if (sortBy === 'createdBy') {
    // Sort by createdBy - could be username or ID, treat as string
    const sorted = orderBy(users, [user => toLower(user.createdBy || '')], [sortOrder]);
    return sorted;
  }

  if (sortBy === 'fullAccountAccessEnabled') {
    // Sort by boolean - false first (0) then true (1)
    const sorted = orderBy(users, [user => (user.fullAccountAccessEnabled ? 1 : 0)], [sortOrder]);
    return sorted;
  }

  // Default sorting for username, email, createdAt - case-insensitive
  const sorted = orderBy(users, [user => {
    const value = user[sortBy];
    return typeof value === 'string' ? toLower(value) : value;
  }], [sortOrder]);
  return sorted;
};

export const filterUsers = (users, filterTerm, accountMap = {}, userMap = {}) => {
  if (!filterTerm || filterTerm.length === 0) {
    return users;
  }

  const lowerFilterTerm = toLower(filterTerm);

  return filter(users, (user) => {
    const username = toLower(user.username || '');
    const email = toLower(user.email || '');

    // Build account search terms from both account and additionalAccounts
    // Include both names and IDs
    const allAccountTerms = [];

    // Helper function to extract account ID
    const getAccountId = (acc) => {
      if (!acc) {return '';}
      if (typeof acc === 'string') {return acc;}
      return acc.id || '';
    };

    // Helper function to get account name from accountMap or fallback to name field
    const getAccountNameFromMap = (acc) => {
      const accId = getAccountId(acc);
      if (!accId) {return '';}

      // First try to get from accountMap
      if (accountMap && accountMap[accId] && accountMap[accId].name) {
        return accountMap[accId].name;
      }
      // Fallback to name field in the account object itself
      if (typeof acc === 'object' && acc.name) {
        return acc.name;
      }

      return '';
    };

    if (user.account) {
      // Add the ID
      const accId = getAccountId(user.account);
      if (accId) {
        allAccountTerms.push(toLower(accId));
      }
      // Add the name
      const accName = getAccountNameFromMap(user.account);
      if (accName) {
        allAccountTerms.push(toLower(accName));
      }
    }
    if (user.additionalAccounts && Array.isArray(user.additionalAccounts)) {
      user.additionalAccounts.forEach(acc => {
        const accId = getAccountId(acc);
        const accName = getAccountNameFromMap(acc);
        if (accId) {allAccountTerms.push(toLower(accId));}
        if (accName) {allAccountTerms.push(toLower(accName));}
      });
    }
    const accounts = allAccountTerms.join(' ');

    // Build createdBy search terms - include both ID and username if available
    const createdByTerms = [];
    if (user.createdBy) {
      createdByTerms.push(toLower(user.createdBy));
      // Also search by username if available in userMap
      if (userMap && userMap[user.createdBy] && userMap[user.createdBy].username) {
        createdByTerms.push(toLower(userMap[user.createdBy].username));
      }
    }
    const createdBy = createdByTerms.join(' ');

    return (
      includes(username, lowerFilterTerm) ||
      includes(email, lowerFilterTerm) ||
      includes(accounts, lowerFilterTerm) ||
      includes(createdBy, lowerFilterTerm)
    );
  });
};

export const getPaginatedDataTable = (users, paginationOptions) => {
  const { selectedPageNumber = 1, elementsPerPage = 50 } = paginationOptions;

  // Calculate pagination
  const totalRecords = users.length;
  const totalTablePages = Math.ceil(totalRecords / elementsPerPage);
  const startIndex = (selectedPageNumber - 1) * elementsPerPage;
  const endIndex = startIndex + elementsPerPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  // Calculate page range info
  const fromPageCount = totalRecords === 0 ? 0 : startIndex + 1;
  const toPageCount = Math.min(endIndex, totalRecords);

  return {
    paginatedUsers,
    selectedPageNumber,
    elementsPerPage,
    totalRecords,
    totalTablePages,
    fromPageCount,
    toPageCount,
    startPaginationRange: 1,
    endPaginationRange: totalTablePages,
  };
};
